import * as React from "react"
import { useEffect, useState, useCallback } from "react"
import {
    Alert,
    AlertTitle,
    Skeleton,
    Collapse,
    Chip,
    Typography,
    Stack,
} from "@mui/material"
import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import HomeIcon from "@mui/icons-material/Home"
import ApartmentIcon from "@mui/icons-material/Apartment"
import GroupIcon from "@mui/icons-material/Group"
import EnergySavingsLeafIcon from "@mui/icons-material/EnergySavingsLeaf"

import PropertySection from "./PropertySection"


// ---------------------------------------------------------------------------
// DPE colour scale (A = best, G = worst)
// ---------------------------------------------------------------------------
const dpeColors = {
    A: "#319530", B: "#32c334", C: "#c5f433", D: "#f3f40b",
    E: "#f4c409", F: "#f49533", G: "#f40a04",
}

function dpeTextColor(hex) {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255
    return lum > 0.55 ? "#000" : "#fff"
}


// ---------------------------------------------------------------------------
// Small helper: display a skeleton while loading, or the real content
// ---------------------------------------------------------------------------
function InfoRow({ icon, label, loading, children }) {
    return (
        <ListItem alignItems="flex-start" sx={{ px: 0 }}>
            <ListItemIcon sx={{ mr: 1.5, mt: 0.5, minWidth: "auto" }}>
                {React.cloneElement(icon, { sx: { width: 20, height: 20, color: "text.secondary" } })}
            </ListItemIcon>
            <ListItemText
                sx={{ m: 0 }}
                disableTypography
                primary={
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
                        {label}
                    </Typography>
                }
                secondary={loading ? <Skeleton variant="text" width="55%" sx={{ mt: 0.5 }} /> : children}
            />
        </ListItem>
    )
}


// ---------------------------------------------------------------------------
// PropertyInfoCard — full detail for the "Bien" tab: address, type,
// ownership split (with shares), DPE.
//
// DECISION: tenants used to live here too. They've moved to
// PropertyCurrentCard, on the "Location" tab — a tenant belongs to the
// lease, not the building. Someone still owns this house after the tenant
// moves out, which is exactly the distinction this redesign is built
// around.
//
// DECISION: fetch logic overlaps with PropertyHeader (both read property,
// dpe, property_owner). That duplication is intentional, not an oversight:
// PropertyHeader needs a permanently-visible two-line summary, this needs
// the full picture with ownership shares, and the two are mounted, loaded
// and torn down completely independently (this one only exists while the
// "Bien" tab is open).
//
// Props
//   supabase    – initialised Supabase client  (createClient(url, anonKey))
//   propertyId  – UUID of the property to display
// ---------------------------------------------------------------------------
export default function PropertyInfoCard({ supabase, propertyId }) {

    // ── State ────────────────────────────────────────────────────────────────
    const [property, setProperty] = useState(null)
    const [dpe,      setDpe]      = useState(null)
    const [owners,   setOwners]   = useState([])

    const [errors,  setErrors]  = useState({})
    const [loading, setLoading] = useState(true)


    // ── Fetch helpers ────────────────────────────────────────────────────────

    const fetchProperty = useCallback(async () => {
        const { data, error } = await supabase
            .from("property")
            .select("*")
            .eq("id", propertyId)
            .single()

        if (error) {
            setErrors(prev => ({ ...prev, property: `Propriété introuvable : ${error.message}` }))
        } else {
            setErrors(prev => { const { property: _, ...rest } = prev; return rest })
            setProperty(data)
        }
    }, [supabase, propertyId])

    const fetchDpe = useCallback(async () => {
        const { data, error } = await supabase
            .from("dpe")
            .select("energy_consumption_category, greenhouse_gas_category, valid_until")
            .eq("property_id", propertyId)
            .order("valid_until", { ascending: false })
            .limit(1)
            .maybeSingle()

        if (error) {
            setErrors(prev => ({ ...prev, dpe: `Impossible de charger le DPE : ${error.message}` }))
        } else {
            setErrors(prev => { const { dpe: _, ...rest } = prev; return rest })
            setDpe(data)
        }
    }, [supabase, propertyId])

    const fetchOwners = useCallback(async () => {
        const { data, error } = await supabase
            .from("property_owner")
            .select(`
                shares,
                person:owner_person_id ( title, first_name, last_name ),
                company:owner_company_id ( name )
            `)
            .eq("property_id", propertyId)

        if (error) {
            setErrors(prev => ({ ...prev, owners: `Impossible de charger les propriétaires : ${error.message}` }))
            return
        }

        setErrors(prev => { const { owners: _, ...rest } = prev; return rest })

        const totalShares = data.reduce((sum, o) => sum + (o.shares ?? 0), 0)
        const formatted = data.map(o => {
            const name = o.person
                ? `${o.person.title} ${o.person.first_name} ${o.person.last_name}`
                : (o.company?.name ?? "Inconnu")
            const pct = totalShares > 0
                ? Math.round(100 * (o.shares ?? 0) / totalShares)
                : 0
            return `${name} (${pct}\u202f%)`
        })
        setOwners(formatted)
    }, [supabase, propertyId])


    // ── Initial load ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!propertyId) return
        setLoading(true)
        setErrors({})
        Promise.all([
            fetchProperty(),
            fetchDpe(),
            fetchOwners(),
        ]).finally(() => setLoading(false))
    }, [propertyId, fetchProperty, fetchDpe, fetchOwners])


    // ── Realtime subscriptions ───────────────────────────────────────────────
    useEffect(() => {
        if (!propertyId) return

        const channel = supabase
            .channel(`property-info-card-${propertyId}`)
            .on("postgres_changes",
                { event: "*", schema: "public", table: "property", filter: `id=eq.${propertyId}` },
                fetchProperty)
            .on("postgres_changes",
                { event: "*", schema: "public", table: "dpe", filter: `property_id=eq.${propertyId}` },
                fetchDpe)
            .on("postgres_changes",
                { event: "*", schema: "public", table: "property_owner", filter: `property_id=eq.${propertyId}` },
                fetchOwners)
            .subscribe()

        return () => { supabase.removeChannel(channel) }
    }, [supabase, propertyId, fetchProperty, fetchDpe, fetchOwners])


    // ── Derived ──────────────────────────────────────────────────────────────
    const hasErrors = Object.keys(errors).length > 0

    const addressLines = [
        property?.address_1,
        property?.address_2,
        [property?.zip, property?.city].filter(Boolean).join("\u2009"),
    ].filter(Boolean)


    // ── DPE chip ─────────────────────────────────────────────────────────────
    function DpeDisplay() {
        if (!dpe) return <Typography variant="body2">Aucun DPE</Typography>

        const cat   = dpe.energy_consumption_category
        const bg    = dpeColors[cat] ?? "#ccc"
        const color = dpeTextColor(bg)

        return (
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Chip
                    label={cat}
                    size="small"
                    sx={{
                        bgcolor:    bg,
                        color:      color,
                        fontWeight: 700,
                        minWidth:   "2rem",
                        "& .MuiChip-label": { px: 1 },
                    }}
                />
                <Typography variant="body2" color="text.secondary">
                    Valide jusqu'au {new Date(dpe.valid_until).toLocaleDateString("fr-FR")}
                </Typography>
            </Stack>
        )
    }


    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <PropertySection title="Détails du bien">

            {/* ── Error banners ── */}
            <Collapse in={hasErrors}>
                {Object.entries(errors).map(([key, message]) => (
                    <Alert severity="error" key={key} sx={{ mb: 1 }}>
                        <AlertTitle>Erreur de chargement</AlertTitle>
                        {message}
                    </Alert>
                ))}
            </Collapse>

            <List dense sx={{ p: 0 }}>

                {/* Address */}
                <InfoRow icon={<HomeIcon />} label="Adresse" loading={loading}>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                        {addressLines.join("\n") || "—"}
                    </Typography>
                </InfoRow>

                {/* Property type */}
                <InfoRow icon={<ApartmentIcon />} label="Type" loading={loading}>
                    <Typography variant="body2">
                        {property?.kind || "—"}
                    </Typography>
                </InfoRow>

                {/* Owners */}
                <InfoRow icon={<GroupIcon />} label="Propriétaire(s)" loading={loading}>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                        {owners.length > 0 ? owners.join("\n") : "Aucun propriétaire"}
                    </Typography>
                </InfoRow>

                {/* DPE */}
                <InfoRow icon={<EnergySavingsLeafIcon />} label="DPE" loading={loading}>
                    <DpeDisplay />
                </InfoRow>

            </List>
        </PropertySection>
    )
}
